import type { LinkNode } from 'components/link/types';
import type { DefaultParams, Params } from 'wouter';
import { LinkSummary } from 'components/link/LinkSummary';
import { StripeLinkDetail } from 'components/link/StripeLinkDetail';
import { UnitLinkDetail } from 'components/link/UnitLinkDetail';
import { useGetLinkQuery } from 'hooks/internalApi';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import React, { useMemo } from 'react';
import { match } from 'ts-pattern';
import { getResultOrError } from 'utils/errors';
import { useWorkspaceId } from 'wrappers/WorkspaceProvider';

import {
  decodeBankLinkId,
  encodeBankLinkId,
} from '@fragment/shared-utils/ids/bankAccount';
import { InlineError } from '@fragment/ui/src/components/InlineError/InlineError';

import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { IntegrationSettingsLayout } from './IntegrationSettingsLayout';

type Props<T extends DefaultParams> = {
  params: Params<T>;
};

export const LinkDetailView = <T extends DefaultParams>({
  params: { linkId },
}: Props<T>) => {
  const { context } = useInternalApiContext();
  const workspaceId = useWorkspaceId();
  const variables = useMemo(
    () => ({
      linkId: decodeBankLinkId(linkId).linkId,
      workspaceId,
    }),
    [linkId, workspaceId]
  );
  const [{ data, error }] = useGetLinkQuery({
    context,
    pause: !context.token,
    variables,
  });
  const { isError: isWorkspaceError, result: workspace } = getResultOrError(
    data?.workspace,
    error
  );
  const { isError: isLinkError, result: link } = getResultOrError(
    workspace?.link,
    error
  );
  const hasError = isWorkspaceError || isLinkError;
  useDocumentTitle(hasError ? 'Link Details' : `Link Details - ${link.name}`);

  const encodedLink = link && {
    ...link,
    id: encodeBankLinkId(link),
  };

  return (
    <IntegrationSettingsLayout
      summarySection={<LinkSummary link={encodedLink as LinkNode} />}
    >
      {hasError || !encodedLink ? (
        <InlineError>Failed to load link</InlineError>
      ) : (
        <div className="space-y-f2 flex flex-col min-h-f0">
          {match(encodedLink)
            .with({ __typename: 'StripeLink' }, (stripeLink) => (
              <StripeLinkDetail
                workspaceId={workspaceId}
                encodedLinkId={stripeLink.id}
                lastUpdatedTs={stripeLink.rakLastUpdated ?? undefined}
              />
            ))
            .with({ __typename: 'UnitLink' }, (unitLink) => (
              <UnitLinkDetail
                workspaceId={workspaceId}
                encodedLinkId={unitLink.id}
                lastUpdatedTs={unitLink.orgTokenLastUpdated ?? undefined}
              />
            ))
            .otherwise(() => null)}
        </div>
      )}
    </IntegrationSettingsLayout>
  );
};
